import React, { useState } from 'react';

import type { EventId } from '@vlabs/api/luna-api/model/EventId';
import { useImage } from '@vlabs/shared/hooks';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import type { WithDataTestid, WithClassNameProp } from '@vlabs/uikit/types';
import cn from 'classnames';

import { FullFrameDetectionsWithSettings } from '@vlabs/monokit/entities/event-details/full-frame-detections-with-settings';
import { useEventApi, useEventQuery } from '@vlabs/monokit/entities/events/api';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { useVmsFromSourceName } from '@vlabs/monokit/widgets/video-archive';

import st from './EventMediaViewer.module.sass';
import { useEventMediaViewerTranslation } from './i18n';

type Tab = 'frame' | 'videoArchive';

const getImageUrl = (imageOrigin?: string | null): string | undefined => (
  imageOrigin?.startsWith('/6/images') ? `/api/lp5${imageOrigin}` : undefined
);

interface EventMediaViewerProps extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
  canViewVideoArchive?: boolean;
}

const displayName = 'EventMediaViewer';
const testid = buildTestid(displayName);

interface VideoArchiveTabProps {
  source?: string | null;
  canViewVideoArchive: boolean;
  tab: Tab;
  onTabChange: (tab: Tab) => void;
  frameDisabled: boolean;
  testId: string | undefined;
}

function VideoArchiveTab({
  source = undefined,
  canViewVideoArchive,
  tab,
  onTabChange,
  frameDisabled,
  testId: dataTestId,
}: VideoArchiveTabProps): React.JSX.Element {
  const { t } = useEventMediaViewerTranslation();
  const { vmsSource } = useVmsFromSourceName(source);

  if (!vmsSource || !canViewVideoArchive) {
    return <h5>{t('tab', { context: 'frame' })}</h5>;
  }

  return (
    <ButtonSwitchGroup
      onChange={onTabChange}
      size="small"
      value={tab}
      variant="square"
      {...testid(dataTestId)}
    >
      <ButtonSwitch
        disabled={frameDisabled}
        value="frame"
      >
        {t('tab', { context: 'frame' })}
      </ButtonSwitch>

      <ButtonSwitch
        disabled
        value="videoArchive"
      >
        {t('tab', { context: 'video_archive' })}
      </ButtonSwitch>
    </ButtonSwitchGroup>
  );
}

function EventMediaViewer({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
  canViewVideoArchive = true,
}: EventMediaViewerProps): React.JSX.Element {
  const { t } = useEventMediaViewerTranslation();
  const api = useEventApi();

  const [tab, setTab] = useState<Tab>('frame');

  const { data: event } = useEventQuery(eventId);

  const detectionWithFullFrame = event?.face_detections?.find(({ image_origin }) => image_origin?.startsWith('/6/images'))
      ?? event?.body_detections?.find(({ image_origin }) => image_origin?.startsWith('/6/images'));
  const fullFrameUrl = getImageUrl(detectionWithFullFrame?.image_origin);
  const fullFrame = useImage(fullFrameUrl);

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      {!!api.capabilities.videoArchive && (
        <VideoArchiveTab
          canViewVideoArchive={canViewVideoArchive}
          frameDisabled={!fullFrame.url}
          onTabChange={setTab}
          source={event?.source}
          tab={tab}
          testId={dataTestId}
        />
      )}
      {!api.capabilities.videoArchive && <h5>{t('tab', { context: 'frame' })}</h5>}

      {tab === 'frame' && !!fullFrame.url && <FullFrameDetectionsWithSettings eventId={eventId} />}
      {/* FIXME табу с видеоархивом добавить в отдельной задаче */}

      {!fullFrame.url && <span className={st.NoData}>{t('no_data')}</span>}
    </div>
  );
}

EventMediaViewer.displayName = displayName;

export { EventMediaViewer };
